import { useState, useEffect, useRef, useCallback } from 'react';
import { Hash, Lock, Users, Settings, Send, Smile, Paperclip, AtSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Message } from './Message';
import { ThreadPanel } from './ThreadPanel';
import { useChannelWebSocket } from '../../hooks/useChannelWebSocket';
import { cn } from '../../lib/utils';
import { BACKEND_URL } from '@/config/api';

export const ChannelView = ({ 
  channel, 
  token, 
  currentUser 
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const {
    isConnected,
    onlineUsers,
    typingUsers,
    setHandlers,
    sendNewMessage,
    sendMessageUpdated,
    sendMessageDeleted,
    sendReactionUpdated,
    sendTyping
  } = useChannelWebSocket(channel?.id, token, currentUser, !!channel);

  // Set up WebSocket handlers
  useEffect(() => {
    setHandlers({
      onNewMessage: (message) => {
        if (message.thread_id) {
          // Update parent message's reply count
          setMessages(prev => prev.map(m => 
            m.id === message.thread_id 
              ? { 
                  ...m, 
                  reply_count: (m.reply_count || 0) + 1,
                  thread_preview: [...(m.thread_preview || []).slice(-2), message]
                }
              : m
          ));
        } else {
          setMessages(prev => [...prev, message]);
        }
      },
      onMessageUpdated: (message) => {
        setMessages(prev => prev.map(m => m.id === message.id ? message : m));
      },
      onMessageDeleted: (messageId) => {
        setMessages(prev => prev.filter(m => m.id !== messageId));
      },
      onReactionUpdated: (messageId, reactions) => {
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, reactions } : m
        ));
      }
    });
  }, [setHandlers]);

  // Fetch messages when channel changes
  useEffect(() => {
    if (channel?.id) {
      fetchMessages();
    }
  }, [channel?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channel.id}/messages`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    sendTyping(false);

    try {
      const response = await fetch(`${BACKEND_URL}/api/channels/${channel.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage })
      });

      if (response.ok) {
        const sentMessage = await response.json();
        setMessages(prev => [...prev, sentMessage]);
        sendNewMessage(sentMessage);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    sendTyping(true);
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing indicator after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendTyping(false);
    }, 2000);
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channel.id}/messages/${messageId}/reactions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ emoji })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.map(m => 
          m.id === messageId ? { ...m, reactions: data.reactions } : m
        ));
        sendReactionUpdated(messageId, data.reactions);
      }
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleEdit = async (messageId, content) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channel.id}/messages/${messageId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content })
        }
      );
      
      if (response.ok) {
        const updated = await response.json();
        setMessages(prev => prev.map(m => m.id === messageId ? updated : m));
        sendMessageUpdated(updated);
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channel.id}/messages/${messageId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        sendMessageDeleted(messageId);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleOpenThread = (message) => {
    setActiveThread(message);
  };

  const handleThreadMessageSent = (message) => {
    // Update parent message's reply count in main view
    setMessages(prev => prev.map(m => 
      m.id === message.thread_id 
        ? { 
            ...m, 
            reply_count: (m.reply_count || 0) + 1,
            thread_preview: [...(m.thread_preview || []).slice(-2), message]
          }
        : m
    ));
    sendNewMessage(message);
  };

  // Check if we should show date divider
  const shouldShowDateDivider = (message, index) => {
    if (index === 0) return true;
    const prevDate = new Date(messages[index - 1].created_at).toDateString();
    const currDate = new Date(message.created_at).toDateString();
    return prevDate !== currDate;
  };

  if (!channel) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#1a1d21] text-gray-400">
        <p>Select a channel to start chatting</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex bg-[#1a1d21]">
      {/* Main Channel Content */}
      <div className="flex-1 flex flex-col" data-testid="channel-view">
        {/* Channel Header */}
        <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {channel.is_private ? (
              <Lock size={18} className="text-gray-400" />
            ) : (
              <Hash size={18} className="text-gray-400" />
            )}
            <h2 className="font-bold text-white text-lg">{channel.name}</h2>
            {channel.topic && (
              <span className="text-sm text-gray-400 ml-4 hidden md:inline">
                {channel.topic}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-gray-400 text-sm">
              <Users size={16} />
              <span>{channel.members?.length || 0}</span>
            </div>
            {isConnected && (
              <div className="w-2 h-2 rounded-full bg-green-500" title="Connected" />
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 px-4">
              <Hash size={48} className="mb-4 opacity-50" />
              <h3 className="text-lg font-bold text-white mb-2">
                Welcome to #{channel.name}!
              </h3>
              <p className="text-center">
                This is the beginning of the #{channel.name} channel.
                {channel.description && ` ${channel.description}`}
              </p>
            </div>
          ) : (
            <div className="py-4">
              {messages.map((message, index) => (
                <Message
                  key={message.id}
                  message={message}
                  currentUserId={currentUser?.id}
                  onReact={handleReaction}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onOpenThread={handleOpenThread}
                  showDateDivider={shouldShowDateDivider(message, index)}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Typing Indicator */}
        {typingUsers.length > 0 && (
          <div className="px-5 py-1 text-sm text-gray-400">
            <span className="inline-flex items-center gap-1">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
              <span className="ml-1">Someone is typing...</span>
            </span>
          </div>
        )}

        {/* Message Input */}
        <div className="px-5 py-4">
          <div className="bg-[#222529] border border-gray-700 rounded-lg focus-within:border-gray-500">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={`Message #${channel.name}`}
              className="w-full px-4 py-3 bg-transparent text-white text-sm resize-none focus:outline-none"
              rows={1}
              style={{ minHeight: '44px', maxHeight: '200px' }}
              data-testid="message-input"
            />
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-700">
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                  <Paperclip size={18} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                  <Smile size={18} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                  <AtSign size={18} />
                </Button>
              </div>
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || isSending}
                size="sm"
                className="bg-[#007a5a] hover:bg-[#006649] text-white disabled:opacity-50"
                data-testid="send-message-btn"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Thread Panel (Slack-style sidebar) */}
      {activeThread && (
        <ThreadPanel
          parentMessage={activeThread}
          channelId={channel.id}
          channelName={channel.name}
          token={token}
          currentUserId={currentUser?.id}
          onClose={() => setActiveThread(null)}
          onReact={handleReaction}
          onMessageSent={handleThreadMessageSent}
        />
      )}
    </div>
  );
};
